import exifr from "exifr";

export type TimeSource =
    | "exif"            // EXIF DateTimeOriginal with explicit OffsetTimeOriginal
    | "exif-local"      // EXIF DateTimeOriginal, no TZ — interpreted as browser-local
    | "gps"             // EXIF GPSDateStamp + GPSTimeStamp (UTC)
    | "filename"        // matched a camera filename pattern (PXL/Samsung/DJI)
    | "whatsapp"        // WhatsApp filename (date only, or send time — unreliable)
    | "video-metadata"  // MediaInfo encoded_date
    | "lastmodified";   // last resort — file.lastModified

export interface FileMetadata {
    timestamp: number;
    timeSource: TimeSource;
    latitude: number | null;
    longitude: number | null;
}

// Source labels considered unreliable enough to badge in the UI for manual review.
export const UNRELIABLE_TIME_SOURCES: ReadonlySet<TimeSource> = new Set<TimeSource>(["lastmodified", "whatsapp"]);

const PXLRegex = /PXL_(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})(\d{3})\.[a-zA-Z]+/;
const SamsungRegex = /(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})\.[a-zA-Z]+/;
const DJIRegex = /DJI_(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})_/;
const DJIFlyRegex = /dji_fly_(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})_/;
// WhatsApp Web download — "WhatsApp Image 2024-03-15 at 14.30.45.jpeg" (send time, in recipient's local TZ)
const WhatsAppWebRegex = /WhatsApp\s+(?:Image|Video)\s+(\d{4})-(\d{2})-(\d{2})\s+at\s+(\d{2})\.(\d{2})\.(\d{2})/;
// WhatsApp Android save — "IMG-20240315-WA0001.jpg" (date only)
const WhatsAppMobileRegex = /^(?:IMG|VID|AUD|PTT)-(\d{4})(\d{2})(\d{2})-WA\d+/;

let mediaInfoInstance: any = null;

async function getMediaInfo() {
    if (!mediaInfoInstance) {
        // @ts-ignore — loaded via <script> in public/index.html
        if (!globalThis.MediaInfo) throw new Error("MediaInfo not loaded");
        // @ts-ignore
        mediaInfoInstance = await globalThis.MediaInfo.mediaInfoFactory({format: "object"});
    }
    return mediaInfoInstance;
}

// Parse the raw EXIF date string "YYYY:MM:DD HH:MM:SS" together with an optional offset
// like "+02:00". Returns a UTC epoch ms, or null if unparseable.
function parseExifWallClock(exifStr: string, offset: string | null): number | null {
    const m = exifStr.match(/^(\d{4}):(\d{2}):(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
    if (!m) return null;
    const iso = `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}${offset ?? ""}`;
    const t = Date.parse(iso);
    return isNaN(t) ? null : t;
}

function dmsToDecimal(dms: any, ref: any): number | null {
    if (!Array.isArray(dms) || dms.length < 3) return null;
    const [d, m, s] = [Number(dms[0]), Number(dms[1]), Number(dms[2])];
    if (![d, m, s].every(Number.isFinite)) return null;
    let decimal = d + m / 60 + s / 3600;
    if (ref === "S" || ref === "W") decimal = -decimal;
    return Number.isFinite(decimal) ? decimal : null;
}

async function getExifMetadata(file: File): Promise<Partial<FileMetadata>> {
    try {
        const data = await exifr.parse(file, {
            ifd0: false, exif: true, gps: true,
            pick: [
                "DateTimeOriginal", "OffsetTimeOriginal",
                "GPSLatitude", "GPSLongitude", "GPSLatitudeRef", "GPSLongitudeRef",
                "GPSDateStamp", "GPSTimeStamp",
            ],
            reviveValues: false,
        } as any);
        if (!data) return {};

        let latitude = Number.isFinite(data.latitude) ? data.latitude : null;
        let longitude = Number.isFinite(data.longitude) ? data.longitude : null;
        if (latitude === null) latitude = dmsToDecimal(data.GPSLatitude, data.GPSLatitudeRef);
        if (longitude === null) longitude = dmsToDecimal(data.GPSLongitude, data.GPSLongitudeRef);

        // GPS UTC is the gold standard when present.
        const gpsTs = data.GPSDateStamp && data.GPSTimeStamp
            ? gpsToUtc(data.GPSDateStamp, data.GPSTimeStamp)
            : null;

        if (data.DateTimeOriginal) {
            const exifStr = String(data.DateTimeOriginal);
            const explicitOffset = typeof data.OffsetTimeOriginal === "string" ? data.OffsetTimeOriginal : null;

            if (explicitOffset) {
                const t = parseExifWallClock(exifStr, explicitOffset);
                if (t !== null) return {timestamp: t, timeSource: "exif", latitude, longitude};
            }
            if (gpsTs !== null) {
                // GPS date+time IS the correct UTC. Use it directly; EXIF wall clock is camera-local.
                return {timestamp: gpsTs, timeSource: "gps", latitude, longitude};
            }
            // No offset, no GPS — interpret EXIF wall clock as browser-local.
            const t = parseExifWallClock(exifStr, null);
            if (t !== null) return {timestamp: t, timeSource: "exif-local", latitude, longitude};
        }

        if (gpsTs !== null) {
            return {timestamp: gpsTs, timeSource: "gps", latitude, longitude};
        }

        return {latitude, longitude};
    } catch (e) {
        console.warn("EXIF parse failed:", e);
        return {};
    }
}

function gpsToUtc(dateStr: any, timeArr: any): number | null {
    // GPSDateStamp is "YYYY:MM:DD"; GPSTimeStamp can be an array [h, m, s] or "h:m:s" string.
    const ds = String(dateStr).match(/^(\d{4}):(\d{2}):(\d{2})/);
    if (!ds) return null;
    let h = 0, m = 0, s = 0;
    if (Array.isArray(timeArr) && timeArr.length >= 3) {
        h = Number(timeArr[0]); m = Number(timeArr[1]); s = Number(timeArr[2]);
    } else if (typeof timeArr === "string") {
        const tm = timeArr.match(/(\d{1,2}):(\d{1,2}):(\d{1,2})/);
        if (!tm) return null;
        h = Number(tm[1]); m = Number(tm[2]); s = Number(tm[3]);
    } else return null;
    if ([h, m, s].some(x => Number.isNaN(x))) return null;
    const iso = `${ds[1]}-${ds[2]}-${ds[3]}T${pad(h)}:${pad(m)}:${pad(Math.floor(s))}Z`;
    const t = Date.parse(iso);
    return isNaN(t) ? null : t;
}
const pad = (n: number) => (n < 10 ? "0" + n : "" + n);

function getDateFromFilename(filename: string): {timestamp: number; timeSource: TimeSource} | null {
    try {
        let m: RegExpMatchArray | null;

        if ((m = filename.match(WhatsAppWebRegex))) {
            const t = Date.parse(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}`);
            if (!isNaN(t)) return {timestamp: t, timeSource: "whatsapp"};
        }
        if ((m = filename.match(WhatsAppMobileRegex))) {
            // Date only — use noon local so it sorts roughly to mid-day on the captured day.
            const t = Date.parse(`${m[1]}-${m[2]}-${m[3]}T12:00:00`);
            if (!isNaN(t)) return {timestamp: t, timeSource: "whatsapp"};
        }
        if ((m = filename.match(PXLRegex))) {
            const t = Date.parse(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}.${m[7]}Z`);
            if (!isNaN(t)) return {timestamp: t, timeSource: "filename"};
        }
        if ((m = filename.match(DJIRegex))) {
            const t = Date.parse(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}`);
            if (!isNaN(t)) return {timestamp: t, timeSource: "filename"};
        }
        if ((m = filename.match(DJIFlyRegex))) {
            const t = Date.parse(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}`);
            if (!isNaN(t)) return {timestamp: t, timeSource: "filename"};
        }
        if ((m = filename.match(SamsungRegex))) {
            const t = Date.parse(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}`);
            if (!isNaN(t)) return {timestamp: t, timeSource: "filename"};
        }
    } catch (e) {
        console.warn("Filename parse failed:", e);
    }
    return null;
}

async function getVideoMetadata(file: File): Promise<number | null> {
    try {
        const mediaInfo = await getMediaInfo();
        const getChunk = (chunkSize: number, offset: number) => new Promise<Uint8Array>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (ev) => {
                if (ev.target?.error) reject(ev.target.error);
                resolve(new Uint8Array(ev.target?.result as ArrayBuffer));
            };
            reader.readAsArrayBuffer(file.slice(offset, offset + chunkSize));
        });
        const result = await mediaInfo.analyzeData(file.size, getChunk);
        const track = result?.media?.track?.[0];
        if (!track) return null;
        for (const field of [track.Encoded_Date, track.Tagged_Date, track.Recorded_Date]) {
            if (!field) continue;
            const utcMatch = String(field).match(/UTC\s+(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2})/);
            if (utcMatch) {
                const t = Date.parse(utcMatch[1].replace(" ", "T") + "Z");
                if (!isNaN(t)) return t;
            }
            const t = Date.parse(String(field));
            if (!isNaN(t)) return t;
        }
    } catch (e) {
        console.warn("MediaInfo failed:", e);
    }
    return null;
}

async function fileToMetadata(file: File): Promise<FileMetadata> {
    const isImage = file.type.startsWith("image/") || /\.(jpe?g|heic|heif|png|webp)$/i.test(file.name);
    const isVideo = file.type.startsWith("video/") || /\.(mp4|mov|m4v|avi|webm)$/i.test(file.name);

    let exif: Partial<FileMetadata> = {};
    if (isImage) exif = await getExifMetadata(file);

    if (exif.timestamp !== undefined && exif.timeSource) {
        return {
            timestamp: exif.timestamp,
            timeSource: exif.timeSource,
            latitude: exif.latitude ?? null,
            longitude: exif.longitude ?? null,
        };
    }

    const filenameResult = getDateFromFilename(file.name);
    if (filenameResult) {
        return {
            timestamp: filenameResult.timestamp,
            timeSource: filenameResult.timeSource,
            latitude: exif.latitude ?? null,
            longitude: exif.longitude ?? null,
        };
    }

    if (isVideo) {
        const videoTs = await getVideoMetadata(file);
        if (videoTs !== null) {
            return {
                timestamp: videoTs,
                timeSource: "video-metadata",
                latitude: exif.latitude ?? null,
                longitude: exif.longitude ?? null,
            };
        }
    }

    return {
        timestamp: file.lastModified,
        timeSource: "lastmodified",
        latitude: exif.latitude ?? null,
        longitude: exif.longitude ?? null,
    };
}

export default fileToMetadata;
